import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig';
import { adminAPI } from '../../services/api';
import GTMFormModal from '../../components/admin/GTMFormModal';
import Toast from '../../components/admin/Toast';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import '../../styles/AdminGlobal.css';
import './Admin.css';

interface GTMConfig {
  id?: number;
  container_id: string;
  container_name: string;
  is_active: boolean;
  environment: string;
  environment_display?: string;
  auth_token: string;
  preview_token: string;
  enable_enhanced_ecommerce: boolean;
  created_at?: string;
  updated_at?: string;
}

const GTMManagement: React.FC = () => {
  const [configs, setConfigs] = useState<GTMConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<GTMConfig | null>(null);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false, message: '', type: 'success'
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const columnDefs: ColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 70, 
      sortable: true,
      cellStyle: { color: '#6b7280', fontWeight: '500' }
    },
    { 
      field: 'container_id', 
      headerName: 'Container ID', 
      sortable: true, 
      filter: true, 
      flex: 1,
      cellStyle: { fontWeight: '600', color: '#1f2937' }
    },
    { 
      field: 'container_name', 
      headerName: 'Name', 
      sortable: true, 
      filter: true, 
      flex: 1,
      cellStyle: { color: '#374151' }
    },
    { 
      field: 'environment_display', 
      headerName: 'Environment', 
      sortable: true, 
      width: 140,
      cellRenderer: (params: any) => {
        const env = params.data.environment?.toLowerCase();
        const colors: any = {
          live: { bg: '#d1fae5', color: '#059669' },
          latest: { bg: '#fef3c7', color: '#d97706' },
          custom: { bg: '#ede9fe', color: '#7c3aed' }
        };
        const style = colors[env] || { bg: '#f3f4f6', color: '#6b7280' };
        return (
          <span style={{
            background: style.bg,
            color: style.color,
            padding: '4px 12px',
            borderRadius: '12px',
            fontWeight: '600',
            fontSize: '12px'
          }}>
            {params.value || params.data.environment}
          </span>
        );
      }
    },
    { 
      field: 'enable_enhanced_ecommerce', 
      headerName: 'E-commerce', 
      width: 120,
      cellRenderer: (params: any) => (
        <span style={{
          background: params.value ? '#dbeafe' : '#f3f4f6',
          color: params.value ? '#2563eb' : '#6b7280',
          padding: '4px 10px',
          borderRadius: '12px',
          fontWeight: '600',
          fontSize: '12px'
        }}>
          {params.value ? 'Enabled' : 'Disabled'}
        </span>
      )
    },
    { 
      field: 'is_active', 
      headerName: 'Status', 
      width: 100,
      cellRenderer: (params: any) => (
        <span style={{
          background: params.value ? '#d1fae5' : '#fee2e2',
          color: params.value ? '#059669' : '#dc2626',
          padding: '4px 12px',
          borderRadius: '12px',
          fontWeight: '600',
          fontSize: '12px'
        }}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              onClick={() => handleEdit(params.data)}
              title="Edit GTM Config"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleDeleteClick(params.data)}
              title="Delete GTM Config"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getGTM();
      let data = response.data || response;
      if (data && data.results && Array.isArray(data.results)) {
        data = data.results;
      }
      const configList = Array.isArray(data) ? data : (data ? [data] : []);
      setConfigs(configList);
    } catch (error) {
      console.error('Failed to load GTM configs:', error);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedConfig(null);
    setIsModalOpen(true);
  };

  const handleEdit = (config: GTMConfig) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (config: GTMConfig) => {
    setSelectedConfig(config);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedConfig) return;
    try {
      await adminAPI.deleteGTM(selectedConfig.id!);
      setToast({ show: true, message: 'GTM configuration deleted successfully', type: 'success' });
      loadConfigs();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete GTM configuration', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedConfig(null);
    }
  };

  const handleSave = async (formData: GTMConfig) => {
    try {
      if (selectedConfig?.id) {
        await adminAPI.updateGTM(selectedConfig.id, formData);
        setToast({ show: true, message: 'GTM configuration updated successfully', type: 'success' });
      } else {
        await adminAPI.createGTM(formData);
        setToast({ show: true, message: 'GTM configuration created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedConfig(null);
      loadConfigs();
      // Clear localStorage to force refresh
      localStorage.removeItem('gtm_config');
    } catch (error) {
      console.error('Save error:', error);
      throw error;
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredConfigs = configs.filter(config =>
    config.container_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    config.container_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Google Tag Manager</h3>
          <p>Configure GTM container for centralized tag management</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>
            + Add GTM Container
          </button>
          <button className="btn btn-secondary" onClick={loadConfigs}>
            🔄 Refresh
          </button>
        </div>
      </div>
      
      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Container ID or Name..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredConfigs.length}</span> container(s) configured
        </div>
      
        <div className="ag-grid-container">
          <AgGridReact
            rowData={filteredConfigs}
            columnDefs={columnDefs}
            theme={themeQuartz}
            defaultColDef={{ resizable: true }}
            animateRows={true}
            pagination={true}
            paginationPageSize={20}
            loading={loading}
          />
        </div>
      </div>

      {/* GTM Form Modal */}
      <GTMFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedConfig(null);
        }}
        onSave={handleSave}
        config={selectedConfig}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Delete GTM Configuration"
        message={`Are you sure you want to delete "${selectedConfig?.container_name || selectedConfig?.container_id}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onClose={() => setIsDeleteDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default GTMManagement;
